/**
 * Premium Content Generators for Beslutsläge
 * 
 * Each calculator has specific premium content logic.
 * These generators create the Lägesbild, Osäkerheter, Beslutsbehov, 
 * Nästa steg, and Beslutsbrev data based on calculation results.
 * 
 * STRUCTURE:
 * - generateAvverkningPremium(result, inputs) -> PremiumContent
 * - generateGallringPremium(result, inputs) -> PremiumContent
 */

// ==========================================
// AVVERKNING PREMIUM CONTENT GENERATOR
// ==========================================
export const generateAvverkningPremium = (result, inputs) => {
  const { volym = 200, prisPerM3 = 1100, avverkningskostnad = 350 } = inputs || {};
  const { bruttoIntakt = 0, totalKostnad = 0, nettoIntakt = 0, marginal = 0 } = result || {};
  
  const marginalNum = parseFloat(marginal) || 0;
  const isHighMargin = marginalNum > 70;
  const isLowMargin = marginalNum < 60;
  const isLargeVolume = volym > 300;
  
  // Typical benchmarks
  const typicalPrice = { min: 900, max: 1300 };
  const typicalCost = { min: 300, max: 400 };
  const isPriceHigh = prisPerM3 > typicalPrice.max;
  const isPriceLow = prisPerM3 < typicalPrice.min;
  const isCostHigh = avverkningskostnad > typicalCost.max;
  const isCostLow = avverkningskostnad < typicalCost.min;

  // 1. LÄGESBILD
  const lagesbild = {
    title: 'Lägesbild',
    summary: isHighMargin 
      ? `Avverkningen visar god lönsamhet med ${marginalNum}% marginal. ${isLargeVolume ? 'Den stora volymen ger förhandlingsutrymme för bättre pris.' : ''}`
      : isLowMargin
      ? `Marginalen på ${marginalNum}% är under typisk nivå för slutavverkning. Kostnaderna bör ses över.`
      : `Avverkningen har normal lönsamhet med ${marginalNum}% marginal, i linje med branschsnitt.`,
    keyPoints: [
      `Volym: ${volym.toLocaleString('sv-SE')} m³`,
      `Nettointäkt: ${nettoIntakt.toLocaleString('sv-SE')} kr`,
      `Marginal: ${marginalNum}%`,
      isPriceHigh ? 'Virkespris: Över normalt' : isPriceLow ? 'Virkespris: Under normalt' : 'Virkespris: Normal nivå'
    ]
  };

  // 2. OSÄKERHETER
  const osakerheter = {
    title: 'Osäkerheter',
    intro: 'Följande faktorer kan påverka slutresultatet:',
    factors: [
      {
        factor: 'Volymuppskattning',
        impact: 'hög',
        direction: 'båda',
        desc: `Verklig avverkad volym kan avvika ±10-15% från uppskattningen. Vid ${volym} m³ motsvarar det ${Math.round(volym * 0.1).toLocaleString('sv-SE')}-${Math.round(volym * 0.15).toLocaleString('sv-SE')} m³ osäkerhet.`
      },
      {
        factor: 'Virkespris',
        impact: isPriceHigh || isPriceLow ? 'hög' : 'medel',
        direction: isPriceLow ? 'upp' : isPriceHigh ? 'ned' : 'båda',
        desc: `Ditt pris ${prisPerM3} kr/m³ är ${isPriceHigh ? 'över' : isPriceLow ? 'under' : 'inom'} typiskt intervall (${typicalPrice.min}-${typicalPrice.max} kr). Marknadspriser varierar med konjunktur och årstid.`
      },
      {
        factor: 'Avverkningskostnad',
        impact: isCostHigh ? 'hög' : 'medel',
        direction: isCostHigh ? 'upp' : 'båda',
        desc: `Din kostnad ${avverkningskostnad} kr/m³ är ${isCostHigh ? 'över' : isCostLow ? 'under' : 'inom'} typiskt intervall (${typicalCost.min}-${typicalCost.max} kr). Terräng, avstånd och entreprenör påverkar.`
      },
      {
        factor: 'Virkeskvalitet',
        impact: 'medel',
        direction: 'båda',
        desc: 'Andel timmer vs massaved påverkar snittpriset kraftigt. Timmer kan ge 30-50% högre pris per m³ än massaved.'
      }
    ]
  };

  // 3. BESLUTSBEHOV
  const beslutsbehov = {
    title: 'Beslutsbehov',
    question: 'Räcker detta underlag?',
    assessments: [
      {
        type: 'budget_planning',
        label: 'Budgetplanering',
        answer: 'ja',
        desc: 'Underlaget ger en rimlig uppskattning för intern planering.'
      },
      {
        type: 'contractor_negotiation',
        label: 'Förhandling med entreprenör',
        answer: 'ja',
        desc: 'Ger utgångspunkt för diskussion om avverkningskostnad.'
      },
      {
        type: 'binding_agreement',
        label: 'Bindande avtal',
        answer: 'nej',
        desc: 'Kräver konkreta offerter och volymuppskattning på plats.'
      },
      {
        type: 'tax_declaration',
        label: 'Skatteplanering',
        answer: 'delvis',
        desc: 'Indikation för periodisering, men slutbelopp varierar.'
      }
    ],
    recommendation: isHighMargin
      ? 'God marginal tyder på rätt tidpunkt – inhämta offerter från minst två entreprenörer.'
      : isLowMargin
      ? 'Marginalen är låg – överväg att avvakta bättre priser eller förhandla kostnaderna.'
      : 'Normal marginal – säkerställ konkurrenskraftiga offerter innan beslut.'
  };

  // 4. NÄSTA STEG
  const nastaSteg = {
    title: 'Nästa steg',
    intro: 'Prioriterade åtgärder baserat på nuvarande läge:',
    steps: [
      {
        priority: 1,
        action: 'Verifiera virkesvolym',
        how: 'Mät eller uppskatta med stämpling/inventering',
        why: 'Volymen är den största osäkerheten – påverkar intäkt direkt'
      },
      {
        priority: 2,
        action: 'Inhämta 2-3 offerter',
        how: 'Kontakta lokala skogsbolag och entreprenörer',
        why: 'Pris och kostnad varierar – konkurrens ger bättre villkor'
      },
      {
        priority: 3,
        action: isLargeVolume ? 'Överväg delad avverkning' : 'Planera tidpunkt',
        how: isLargeVolume ? 'Avverka i etapper för att sprida intäkt' : 'Vinterhygge ger ofta lägre kostnad',
        why: isLargeVolume ? 'Skatteplanering och marknadstiming' : 'Markförhållanden påverkar kostnad'
      },
      {
        priority: 4,
        action: 'Granska sortimentsfördelning',
        how: 'Uppskatta andel timmer vs massaved',
        why: 'Timmerandel avgör snittpriset'
      }
    ]
  };

  // 5. BESLUTSBREV
  const beslutsbrev = {
    title: 'Sammanfattning',
    date: new Date().toLocaleDateString('sv-SE'),
    sections: {
      arende: `Avverkningskalkyl: ${volym.toLocaleString('sv-SE')} m³`,
      resultat: `Nettointäkt: ${nettoIntakt.toLocaleString('sv-SE')} kr (marginal ${marginalNum}%)`,
      position: isHighMargin ? 'God lönsamhet' : isLowMargin ? 'Låg marginal' : 'Normal lönsamhet',
      huvudosakerheter: 'Volymuppskattning, virkespris, avverkningskostnad',
      beslutslage: beslutsbehov.recommendation,
      prioriteradAtgard: nastaSteg.steps[0].action
    }
  };

  return { lagesbild, osakerheter, beslutsbehov, nastaSteg, beslutsbrev };
};

// ==========================================
// GALLRING PREMIUM CONTENT GENERATOR
// ==========================================
export const generateGallringPremium = (result, inputs) => {
  const { 
    bestandsvolym = 200, 
    uttagProcent = 30, 
    prisPerM3 = 800, 
    gallringskostnad = 280 
  } = inputs || {};
  
  const { 
    uttagenVolym = 0, 
    kvarvarandeVolym = 0, 
    bruttoIntakt = 0, 
    kostnad = 0, 
    nettoIntakt = 0 
  } = result || {};
  
  const uttagenNum = parseFloat(uttagenVolym) || 0;
  const marginal = bruttoIntakt > 0 ? ((nettoIntakt / bruttoIntakt) * 100) : 0;
  const isHighExtraction = uttagProcent > 35;
  const isLowExtraction = uttagProcent < 25;
  const isPositiveResult = nettoIntakt > 0;
  
  // Benchmarks
  const typicalPrice = { min: 600, max: 900 };
  const typicalCost = { min: 250, max: 350 };
  const isPriceHigh = prisPerM3 > typicalPrice.max;
  const isPriceLow = prisPerM3 < typicalPrice.min;
  const isCostHigh = gallringskostnad > typicalCost.max;

  // 1. LÄGESBILD
  const lagesbild = {
    title: 'Lägesbild',
    summary: isPositiveResult
      ? `Gallringen visar ${isHighExtraction ? 'kraftigt' : 'normalt'} uttag med positivt nettoresultat. ${isPriceLow ? 'Priset är lägre än typiskt för gallringsvirke.' : ''}`
      : `Gallringen ger negativt netto. ${isCostHigh ? 'Kostnaderna är höga.' : ''} Överväg att avvakta bättre priser eller minska uttaget.`,
    keyPoints: [
      `Uttag: ${uttagenNum.toLocaleString('sv-SE')} m³/ha (${uttagProcent}%)`,
      `Kvarvarande: ${parseFloat(kvarvarandeVolym).toLocaleString('sv-SE')} m³/ha`,
      `Nettointäkt: ${nettoIntakt.toLocaleString('sv-SE')} kr`,
      isHighExtraction ? 'Uttag: Kraftigt (>35%)' : isLowExtraction ? 'Uttag: Lågt (<25%)' : 'Uttag: Normal nivå (25-35%)'
    ]
  };

  // 2. OSÄKERHETER
  const osakerheter = {
    title: 'Osäkerheter',
    intro: 'Följande faktorer kan påverka slutresultatet:',
    factors: [
      {
        factor: 'Beståndsvolym uppskattad',
        impact: 'hög',
        direction: 'båda',
        desc: `Vid ${bestandsvolym} m³/ha innebär ±15% osäkerhet en avvikelse på ${Math.round(bestandsvolym * 0.15)} m³/ha i uttag.`
      },
      {
        factor: 'Gallringsvirke pris',
        impact: isPriceLow ? 'hög' : 'medel',
        direction: isPriceLow ? 'upp' : 'båda',
        desc: `Ditt pris ${prisPerM3} kr/m³ är ${isPriceHigh ? 'högt' : isPriceLow ? 'lågt' : 'normalt'} för gallringsvirke (typiskt ${typicalPrice.min}-${typicalPrice.max} kr). Slutavverkningsvirke ger 30-50% mer.`
      },
      {
        factor: 'Gallringskostnad',
        impact: isCostHigh ? 'hög' : 'medel',
        direction: isCostHigh ? 'ned' : 'båda',
        desc: `Din kostnad ${gallringskostnad} kr/m³ är ${isCostHigh ? 'över' : 'inom'} typiskt intervall (${typicalCost.min}-${typicalCost.max} kr). Tät skog ökar kostnaden.`
      },
      {
        factor: 'Tillväxteffekt',
        impact: 'medel',
        direction: 'upp',
        desc: `Kvarvarande ${parseFloat(kvarvarandeVolym).toFixed(0)} m³/ha växer snabbare efter gallring. Kan ge 15-25% ökad tillväxt – en dold intäkt.`
      }
    ]
  };

  // 3. BESLUTSBEHOV
  const beslutsbehov = {
    title: 'Beslutsbehov',
    question: 'Räcker detta underlag?',
    assessments: [
      {
        type: 'timing_decision',
        label: 'Tidpunkt för gallring',
        answer: 'ja',
        desc: 'Underlaget ger en rimlig bild av ekonomin för planering.'
      },
      {
        type: 'extraction_level',
        label: 'Uttagsnivå',
        answer: 'delvis',
        desc: 'Optimal nivå beror på beståndets faktiska täthet och ålder.'
      },
      {
        type: 'contractor_selection',
        label: 'Val av entreprenör',
        answer: 'nej',
        desc: 'Kräver konkreta offerter för jämförelse.'
      },
      {
        type: 'subsidy_application',
        label: 'Bidragsansökan',
        answer: 'nej',
        desc: 'Röjnings-/gallringsbidrag kräver besiktning.'
      }
    ],
    recommendation: isPositiveResult
      ? isHighExtraction
        ? 'Positivt netto men högt uttag – säkerställ att beståndet tål gallringen.'
        : 'God ekonomi – inhämta offerter och planera tidpunkt.'
      : 'Negativt netto – avvakta bättre priser eller överväg mindre uttag.'
  };

  // 4. NÄSTA STEG
  const nastaSteg = {
    title: 'Nästa steg',
    intro: 'Prioriterade åtgärder baserat på nuvarande läge:',
    steps: [
      {
        priority: 1,
        action: 'Verifiera beståndsvolym',
        how: 'Mät eller uppskatta med relaskop på 5-8 provytor',
        why: 'Volym avgör om gallring är motiverat och hur stort uttaget bör vara'
      },
      {
        priority: 2,
        action: 'Bedöm gallringsbehov',
        how: 'Granska stamtäthet, krontak och trädkvalitet',
        why: `Med ${uttagProcent}% uttag kvarstår ${parseFloat(kvarvarandeVolym).toFixed(0)} m³/ha – säkerställ att detta ger optimal tillväxt`
      },
      {
        priority: 3,
        action: isPositiveResult ? 'Inhämta offerter' : 'Avvakta bättre priser',
        how: isPositiveResult ? 'Kontakta 2-3 entreprenörer med erfarenhet av gallring' : 'Följ virkespriser kvartalsvis via Skogsstyrelsen',
        why: isPositiveResult ? 'Konkurrens ger bättre villkor – pris kan variera 20-30%' : 'Gallring är sällan tidskritisk – vänta på bättre marknad'
      },
      {
        priority: 4,
        action: 'Planera för framtida värde',
        how: 'Beräkna förväntad slutavverkning 15-25 år efter gallring',
        why: `Kvarvarande ${parseFloat(kvarvarandeVolym).toFixed(0)} m³/ha kan växa till 300-400 m³/ha vid slutavverkning – det verkliga värdet`
      }
    ]
  };

  // 5. BESLUTSBREV
  const beslutsbrev = {
    title: 'Sammanfattning',
    date: new Date().toLocaleDateString('sv-SE'),
    sections: {
      arende: `Gallringskalkyl: ${bestandsvolym} m³/ha, uttag ${uttagProcent}%`,
      resultat: `Nettointäkt: ${nettoIntakt.toLocaleString('sv-SE')} kr (${uttagenNum} m³/ha)`,
      position: isPositiveResult ? 'Positiv ekonomi' : 'Negativ ekonomi',
      huvudosakerheter: 'Beståndsvolym, gallringsvirke pris, kostnad',
      beslutslage: beslutsbehov.recommendation,
      prioriteradAtgard: nastaSteg.steps[0].action
    }
  };

  return { lagesbild, osakerheter, beslutsbehov, nastaSteg, beslutsbrev };
};

// ==========================================
// FACTORY FUNCTION
// ==========================================
export const generatePremiumContent = (calculatorId, result, inputs) => {
  switch (calculatorId) {
    case 'avverkning':
      return generateAvverkningPremium(result, inputs);
    case 'gallring':
      return generateGallringPremium(result, inputs);
    case 'fastighetsvarde':
      // Existing implementation in BeslutslagePremium.jsx
      return null;
    default:
      console.warn(`No premium generator for calculator: ${calculatorId}`);
      return null;
  }
};

export default {
  generateAvverkningPremium,
  generateGallringPremium,
  generatePremiumContent
};
